/**
 * Retrieves the required L2 selfie handoff from Portal API.
 */

import type { SelfieHandoffErrorMessage } from '../types/pipelineRecovery';
import {
  isSelfieHandoffErrorMessage,
  SelfieHandoffExpiredError,
  SelfieHandoffFetchError,
  SelfieHandoffUnavailableError,
} from '../types/pipelineRecovery';
import { logger } from '../utils/logger';

const serviceLogger = logger.clone({
  labels: { component: 'BestSelfieService' },
});

export interface BestSelfieResponse {
  sessionId: string;
  bestSelfie: string;
  timestamp: string;
  artifactStatus: 'available';
}

type FetchImplementation = typeof fetch;
type DelayImplementation = (delayMs: number) => Promise<void>;

const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_REQUEST_TIMEOUT_MS = 5_000;
const INITIAL_RETRY_DELAY_MS = 500;
const MAX_RETRY_DELAY_MS = 2_000;

// fetch must not be stored unbound: calling it with the service instance as
// `this` throws "Illegal invocation" in browsers, failing every read.
const defaultFetch: FetchImplementation = (...args) => fetch(...args);

const delay: DelayImplementation = (delayMs) =>
  new Promise((resolve) => setTimeout(resolve, delayMs));

const isAvailableResponse = (value: unknown): value is BestSelfieResponse => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const response = value as Record<string, unknown>;
  return (
    typeof response.sessionId === 'string' &&
    typeof response.bestSelfie === 'string' &&
    typeof response.timestamp === 'string' &&
    response.artifactStatus === 'available'
  );
};

export class BestSelfieService {
  private fetchPromises = new Map<string, Promise<BestSelfieResponse>>();

  constructor(
    private readonly fetchImplementation: FetchImplementation = defaultFetch,
    private readonly wait: DelayImplementation = delay,
    private readonly requestTimeoutMs = DEFAULT_REQUEST_TIMEOUT_MS
  ) {}

  /**
   * Retrieve the selfie with a bounded number of attempts. Concurrent callers
   * for the same session share one request sequence to prevent duplicate work.
   */
  async fetchBestSelfie(
    sessionId: string,
    apiUrl: string,
    sessionToken?: string,
    maxAttempts: number = DEFAULT_MAX_ATTEMPTS
  ): Promise<string> {
    if (!sessionId || !apiUrl || !Number.isInteger(maxAttempts) || maxAttempts < 1) {
      throw new SelfieHandoffFetchError();
    }

    const existingPromise = this.fetchPromises.get(sessionId);
    if (existingPromise) {
      serviceLogger.debug('Joining in-flight selfie handoff request');
      return (await existingPromise).bestSelfie;
    }

    const fetchPromise = this.performFetch(
      sessionId,
      apiUrl,
      sessionToken,
      maxAttempts
    );
    this.fetchPromises.set(sessionId, fetchPromise);

    try {
      const result = await fetchPromise;
      return result.bestSelfie;
    } finally {
      this.fetchPromises.delete(sessionId);
    }
  }

  private async performFetch(
    sessionId: string,
    apiUrl: string,
    sessionToken: string | undefined,
    maxAttempts: number
  ): Promise<BestSelfieResponse> {
    const endpoint = `${apiUrl.replace(/\/$/, '')}/api/v1/sessions/${sessionId}/best-selfie`;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        const response = await this.fetchOnce(endpoint, sessionToken);

        if (response.ok) {
          const payload: unknown = await response.json();
          if (!isAvailableResponse(payload)) {
            throw new SelfieHandoffFetchError(response.status);
          }

          serviceLogger.info('Selfie handoff retrieved', {
            labels: { attempt: `${attempt}` },
          });
          return payload;
        }

        const errorResponse = await this.readErrorResponse(response);
        if (!errorResponse) {
          throw new SelfieHandoffFetchError(response.status);
        }

        if (
          errorResponse.type === 'error' &&
          errorResponse.artifactStatus === 'expired'
        ) {
          throw new SelfieHandoffExpiredError(response.status);
        }
        if (errorResponse.type === 'error') {
          throw new SelfieHandoffFetchError(response.status);
        }

        const artifactStatus =
          errorResponse.artifactStatus ??
          'temporarily_unavailable';
        const unavailable = new SelfieHandoffUnavailableError(
          artifactStatus,
          errorResponse.retryTarget,
          attempt
        );

        if (attempt === maxAttempts) {
          throw unavailable;
        }

        serviceLogger.warn('Selfie handoff interrupted; retrying', {
          labels: {
            artifactStatus,
            attempt: `${attempt}`,
            maxAttempts: `${maxAttempts}`,
          },
        });
        await this.wait(this.retryDelay(attempt));
      } catch (error) {
        if (
          error instanceof SelfieHandoffUnavailableError ||
          error instanceof SelfieHandoffExpiredError ||
          error instanceof SelfieHandoffFetchError
        ) {
          throw error;
        }

        if (attempt === maxAttempts) {
          throw new SelfieHandoffUnavailableError(
            'temporarily_unavailable',
            'selfie_handoff',
            attempt
          );
        }

        serviceLogger.warn('Selfie handoff request interrupted; retrying', {
          labels: {
            errorClass: error instanceof Error ? error.name : typeof error,
            attempt: `${attempt}`,
            maxAttempts: `${maxAttempts}`,
          },
        });
        await this.wait(this.retryDelay(attempt));
      }
    }

    throw new SelfieHandoffUnavailableError(
      'temporarily_unavailable',
      'selfie_handoff',
      maxAttempts
    );
  }

  private async fetchOnce(
    endpoint: string,
    sessionToken?: string
  ): Promise<Response> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), this.requestTimeoutMs);
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (sessionToken) {
      headers.Authorization = `Bearer ${sessionToken}`;
    }

    try {
      return await this.fetchImplementation(endpoint, {
        method: 'GET',
        headers,
        credentials: 'include',
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timeout);
    }
  }

  private async readErrorResponse(
    response: Response
  ): Promise<SelfieHandoffErrorMessage | null> {
    try {
      const payload: unknown = await response.json();
      return isSelfieHandoffErrorMessage(payload) ? payload : null;
    } catch {
      return null;
    }
  }

  private retryDelay(attempt: number): number {
    return Math.min(
      INITIAL_RETRY_DELAY_MS * 2 ** Math.max(0, attempt - 1),
      MAX_RETRY_DELAY_MS
    );
  }

  /**
   * Retained for callers that reset flow-local transfer state. Authoritative
   * artifact reads are no longer cached by this service.
   */
  clearCache(): void {
    // Intentionally empty: in-flight work remains fenced by its owner.
  }

  getCacheSize(): number {
    return 0;
  }
}

export const bestSelfieService = new BestSelfieService();
